/**
 * Logica di calcolo per i codici sconto
 * Funzioni pure per gestire validazione, calcolo totali e manipolazione lista codici
 */

import { number, subtract, sum, larger, smallerEq, round } from "mathjs"

/**
 * Costruisce la stringa di codici separati da virgola per la chiamata API
 * @param {Array} discount_codes - Array di oggetti codice sconto
 * @returns {string} Stringa di codici separati da virgola
 */
export function build_codes_string(discount_codes) {
  if (!discount_codes || discount_codes.length === 0) return ''
  return discount_codes.map(code => code.discountCode).join(',')
}

/**
 * Verifica se un codice può essere aggiunto alla lista (non duplicato)
 * @param {Array} discount_codes - Array di codici esistenti
 * @param {string} new_code - Codice da verificare
 * @returns {boolean} true se il codice può essere aggiunto
 */
export function can_add_code(discount_codes, new_code) {
  if (!new_code) return false
  return !discount_codes.some(dc => dc.discountCode === new_code)
}

/**
 * Aggiunge un nuovo codice alla lista
 * @param {Array} discount_codes - Array di codici esistenti
 * @param {string} new_code - Codice da aggiungere
 * @returns {Array} Nuovo array con il codice aggiunto (o stesso array se duplicato)
 */
export function add_code_to_list(discount_codes, new_code) {
  if (!can_add_code(discount_codes, new_code)) {
    return discount_codes
  }
  return [...discount_codes, { discountCode: new_code }]
}

/**
 * Rimuove un codice dalla lista
 * @param {Array} discount_codes - Array di codici esistenti
 * @param {string} code_to_remove - Codice da rimuovere
 * @returns {Array} Nuovo array senza il codice rimosso
 */
export function remove_code_from_list(discount_codes, code_to_remove) {
  return discount_codes.filter(dc => dc.discountCode !== code_to_remove)
}

/**
 * Normalizza i valori numerici di un codice sconto
 * @param {Object} code - Oggetto codice sconto
 * @returns {Object} Codice con valori normalizzati
 */
function normalize_code_values(code) {
  return {
    ...code,
    valueNominal: typeof code.valueNominal === "string" ? number(code.valueNominal) : code.valueNominal,
    valueResidual: typeof code.valueResidual === "string" ? number(code.valueResidual) : code.valueResidual,
  }
}

/**
 * Calcola quanto di un singolo codice può essere usato
 * @param {Object} code - Codice sconto (normalizzato)
 * @param {number} remaining_amount - Importo rimanente da coprire
 * @returns {Object} { valueUsed, remaining } - valore usato e importo rimanente
 */
function calculate_single_code_usage(code, remaining_amount) {
  // Codice non usabile o importo già coperto
  if (!code.usable || remaining_amount === 0) {
    return { valueUsed: 0, remaining: remaining_amount }
  }

  // Non frazionabile: usa tutto solo se copre parzialmente/totalmente
  if (!code.divisible) {
    if (smallerEq(code.valueResidual, remaining_amount)) {
      return {
        valueUsed: code.valueResidual,
        remaining: subtract(remaining_amount, code.valueResidual)
      }
    }
    return { valueUsed: 0, remaining: remaining_amount }
  }

  // Frazionabile: usa quanto necessario
  if (smallerEq(code.valueResidual, remaining_amount)) {
    // Importo minore o uguale, uso tutto
    return {
      valueUsed: code.valueResidual,
      remaining: subtract(remaining_amount, code.valueResidual)
    }
  } else if (larger(code.valueResidual, remaining_amount)) {
    // Importo maggiore, uso solo quanto basta
    return {
      valueUsed: remaining_amount,
      remaining: 0
    }
  }

  return { valueUsed: 0, remaining: remaining_amount }
}

/**
 * Calcola i totali dei codici sconto
 * @param {Object} params - Parametri per il calcolo
 * @param {Array} params.discount_codes - Array di codici sconto
 * @param {number} params.discount_price - Prezzo totale della prenotazione
 * @param {number} params.availability - Credito welfare disponibile
 * @returns {Object} { discount_codes_amount, updated_codes } - Totale usato e codici aggiornati
 */
export function calculate_totals({ discount_codes, discount_price, availability }) {
  let discount_codes_amount = 0

  // Calcola l'importo da coprire con i codici (prezzo - credito welfare)
  const effective_availability = larger(number(availability), 0)
    ? Math.min(number(availability), discount_price)
    : 0
  let remaining_amount = subtract(discount_price, effective_availability)

  // Calcola valueUsed per ogni codice
  const updated_codes = discount_codes.map(code => {
    const normalized = normalize_code_values(code)
    const { valueUsed, remaining } = calculate_single_code_usage(normalized, remaining_amount)

    remaining_amount = remaining
    discount_codes_amount = sum(discount_codes_amount, valueUsed)

    return {
      ...normalized,
      valueUsed
    }
  })

  return {
    discount_codes_amount: round(discount_codes_amount, 2),
    updated_codes
  }
}

/**
 * Tipi di errore per i codici sconto
 */
const KNOWN_ERROR_TYPES = ['NOT FOUND', 'EXPIRED', 'EMPTY', 'DISABLED', 'NOT CUMULABLE']

/**
 * Ritorna il tipo di errore per un codice sconto
 * @param {Object} code - Oggetto codice sconto
 * @returns {string} Tipo di errore ('NOT FOUND' | 'EXPIRED' | 'EMPTY' | 'DISABLED' | 'NOT CUMULABLE' | 'NOT_DIVISIBLE' | 'DEFAULT')
 */
export function get_code_error_type(code) {
  if (KNOWN_ERROR_TYPES.includes(code.status)) {
    return code.status
  }

  // Caso speciale: non frazionabile con valueUsed = 0
  if (!code.divisible && code.valueUsed === 0) {
    return 'NOT_DIVISIBLE'
  }

  return 'DEFAULT'
}

/**
 * Ritorna le informazioni di visualizzazione per un codice sconto
 * @param {Object} code - Oggetto codice sconto
 * @returns {Object} Oggetto con flag di visualizzazione e valori
 */
export function get_code_display_info(code) {
  const is_valid = code.status === 'VALID'
  const value_after = is_valid ? round(subtract(code.valueResidual, code.valueUsed), 2) : 0

  return {
    // Flag di visualizzazione
    show_error: code.valueUsed === 0,
    show_not_divisible: !code.divisible && is_valid,
    show_not_cumulable: code.cumulable === false,
    show_values: is_valid,

    // Valori formattati
    value_residual: is_valid ? round(code.valueResidual, 2) : 0,
    value_after: value_after,
  }
}